import type { Response, Template } from 'lib-shared-legacy-types';
import type { Holiday, OpeningHours, Schedule } from 'lib-shared-types-api';
import type { PropsWithChildren } from 'lib-shared-types-utils';
import sortBy from 'lodash/sortBy';
import moment from 'moment-timezone';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useEffectOnce, useSetState } from 'react-use';
import { v4 as uuid } from 'uuid';

import { ajaxSend } from 'api/api';
import { flowsApi } from 'apis/flows';
import { uploadsApi } from 'apis/uploads';
import type { AnnouncementItem } from 'components/shared/announcementPicker/adapters/announcementAdapter';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { getDateAsUTC } from 'utils/date';
import { useAppPathParams } from 'utils/url/useAppPathParams';

export type ViewType = 'edit' | 'preview';
export type TabType = 'businessHours' | 'exceptions';
export type MenuType = 'hours' | 'audioMessages' | 'autoReplies';

export type TemplatesGetItemsResponse = {
  data: {
    templates: Template[];
  };
  meta: {
    next_page: string | null;
    prev_page: string | null;
  };
};

export type ActiveView = {
  view: ViewType;
  tab: TabType;
  menu: MenuType;
};

export type Errors = (null | string)[][];
export type ModifiedListItem = Record<string, OpeningHours>;
type ScheduleContext = {
  activeView: ActiveView;
  autoReply: null | Schedule['auto_reply_template_id'];
  conflicts: ModifiedListItem;
  holidays: readonly Holiday[];
  initialSchedule: null | Schedule;
  intervalsErrors: Record<ListItemType, Errors>;
  listItems: Record<ListItemType, OpeningHours[]>;
  meta: Record<'isError', boolean>;
  modifiedListItems: Record<ListItemType, ModifiedListItem>;
  announcements: AnnouncementItem[] | null;
  templates: Template[];
  fetchTemplates: VoidFunction;
  refetchAudioMessages: VoidFunction;
  getHolidays: (countryCode: string, timezone: string) => void;
  refetchSchedules: VoidFunction;
  setActiveView: (activeView: ActiveView) => void;
  setAutoReply: (autoReply: null | string) => void;
  setInitialState: VoidFunction;
  setIntervalsErrors: (type: ListItemType, errors: Errors) => void;
  setModifiedListItems: (type: ListItemType, items: ModifiedListItem) => void;
};

type State = Pick<
  ScheduleContext,
  'activeView' | 'autoReply' | 'conflicts' | 'listItems' | 'modifiedListItems'
>;

export enum ListItemType {
  Standard = 'Standard',
  Exception = 'Exception',
}

const initialState: State = {
  activeView: {
    view: 'edit',
    tab: 'businessHours',
    menu: 'hours',
  },
  autoReply: null,
  conflicts: {},
  listItems: {
    [ListItemType.Standard]: [],
    [ListItemType.Exception]: [],
  },
  modifiedListItems: {
    [ListItemType.Standard]: {},
    [ListItemType.Exception]: {},
  },
};

export const ScheduleContext = createContext<ScheduleContext | null>(null);

function sortOpeningHourItems(
  openingHours: readonly OpeningHours[],
  filter: 'exception' | 'standard',
): OpeningHours[] {
  return filter === 'exception'
    ? sortBy(
        openingHours.filter(
          ({ opening_hour_type }) => opening_hour_type !== 'regular',
        ),
        ['next_date'],
      )
    : sortBy(
        openingHours.filter(
          ({ opening_hour_type }) => opening_hour_type === 'regular',
        ),
        [({ recurring }) => recurring?.day_of_week],
      );
}

function getConflicts(exceptions: OpeningHours[]): ModifiedListItem {
  return exceptions.reduce((conflicts, exception) => {
    // Check if the exception has the same next_date as any other one.
    //
    // If the opening hour item already has an `overridden_by` property declared, and
    // the `date` is the same as the inspected exception's `next_date`, then the
    // item is overridden already, so this will be evaludated `false`.
    const getHasConflict = () => {
      const hasOverriddenBy = !!exception.overridden_by?.id;

      return exceptions.some(({ id, overridden_by, next_date }) => {
        const isOverlap =
          exception.id !== id &&
          moment.utc(exception.next_date).isSame(moment.utc(next_date), 'day');

        if (!isOverlap) return false;

        if (hasOverriddenBy) {
          const isOverriddenBy = exception.overridden_by?.id === id;
          const isOverridingActive = moment
            .utc(next_date)
            .isSame(moment.utc(exception.overridden_by?.date), 'day');

          return isOverriddenBy && !isOverridingActive;
        }

        const isOverriding = exception.id === overridden_by?.id;
        const isOverridingActive = moment
          .utc(overridden_by?.date)
          .isSame(moment.utc(exception.next_date), 'day');

        return !isOverriding || (isOverriding && !isOverridingActive);
      });
    };

    const hasConflict = getHasConflict();

    return {
      ...conflicts,
      ...(hasConflict && {
        [exception.id]: exception,
      }),
    };
  }, {});
}

export function ScheduleProvider({ children }: PropsWithChildren): JSX.Element {
  const [
    { activeView, autoReply, conflicts, listItems, modifiedListItems },
    setState,
  ] = useSetState<State>(initialState);
  const [intervalsErrors, setIntervalsErrors] = useState<
    ScheduleContext['intervalsErrors']
  >({
    [ListItemType.Standard]: [],
    [ListItemType.Exception]: [],
  });
  const [templates, setTemplates] = useState<Template[]>([]);
  const notify = useNotifications();

  const { data: announcementData, refetch: refetchAudioMessages } =
    uploadsApi.useReadAnnouncementsQuery();
  const [readHolidays, { data: holidaysResponse }] =
    flowsApi.useLazyReadFlowsSchedulesHolidaysQuery();

  const announcements = useMemo(() => {
    const uploads = announcementData?.data?.uploads;

    return uploads
      ? uploads.map(({ url, pretty_name, created_by }) => ({
          url,
          author: created_by?.name || '',
          label: pretty_name,
        }))
      : null;
  }, [announcementData]);
  const { id } = useAppPathParams('/settings/businesshours/:id');
  const [readFlowSchedule, { data: scheduleData = null, isError }] =
    flowsApi.useLazyReadFlowScheduleQuery();

  const handleFetchSchedule = useCallback(() => {
    readFlowSchedule({ id });
  }, [id, readFlowSchedule]);

  useEffectOnce(() => {
    handleFetchSchedule();
    handleFetchTemplates();
  });

  const handleFetchHolidays = useCallback(
    (countryCode: string, timezone: string) => {
      readHolidays({ country_code: countryCode, timezone });
    },
    [readHolidays],
  );

  useEffect(() => {
    const { country_code, timezone } = scheduleData || {};
    const hasHolidays = !!holidaysResponse?.data?.holidays.length;

    if (country_code && timezone && !hasHolidays) {
      handleFetchHolidays(country_code, timezone);
    }
  }, [holidaysResponse, scheduleData, handleFetchHolidays]);

  useEffect(() => {
    if (scheduleData) {
      const { opening_hours: openingHours } = scheduleData;
      const sortedExceptions = sortOpeningHourItems(openingHours, 'exception');

      setState({
        conflicts: getConflicts(sortedExceptions),
        autoReply: scheduleData.auto_reply_template_id,
        listItems: {
          [ListItemType.Standard]: sortOpeningHourItems(
            openingHours,
            'standard',
          ),
          [ListItemType.Exception]: sortedExceptions,
        },
      });
    }
  }, [scheduleData, setState]);

  const handleSetModifiedListItems = useCallback(
    (type: ListItemType, modifiedItems: ModifiedListItem) => {
      const initialItems = scheduleData?.opening_hours.reduce(
        (acc, curr) => ({ ...acc, ...{ [curr.id]: curr } }),
        {},
      );

      setState((prevState) => {
        const newListItems = [...prevState.listItems[type]];
        const newModifiedListItems = { ...prevState.modifiedListItems[type] };
        // We have to keep track if an entry got a new id, in case of archiving.
        const newIds: Record<string, string> = {};

        Object.entries(modifiedItems).forEach(([id, modifiedItem]) => {
          const { start_date: startDate } = modifiedItem;
          const listItemIndex = newListItems.findIndex(
            ({ id: itemId }) => itemId === id,
          );
          const initialItem = initialItems?.[id];

          const isSameDay = moment.utc(startDate).isSame(moment.utc(), 'day');
          const isPastDay = moment.utc().isAfter(startDate);
          const shouldCreateNewEntry = !isSameDay && isPastDay;

          // If item was deleted
          if (modifiedItem.archived) {
            newListItems.splice(listItemIndex, 1);

            if (initialItem) {
              newModifiedListItems[id] = modifiedItem;

              // If the archived (deleted) exception previously resolved a conflict,
              // the overridden items' `overridden_by` property has to be removed,
              // since it will be referring to a non-existent item.
              newListItems.forEach(
                ({ overridden_by, ...overriddenException }) => {
                  if (overridden_by?.id === id) {
                    const overriddenExceptionListItemIndex =
                      newListItems.findIndex(
                        ({ id: itemId }) => itemId === overriddenException.id,
                      );

                    newModifiedListItems[overriddenException.id] =
                      overriddenException;
                    newListItems[overriddenExceptionListItemIndex] =
                      overriddenException;
                  }
                },
              );
            } else {
              // If the item was created after the initial render,
              // we don't send it to the backend. We remove it from
              // the modified items object.
              delete newModifiedListItems[id];
            }
          } else {
            // If item is already in the list
            if (listItemIndex >= 0) {
              if (shouldCreateNewEntry && initialItem) {
                const newId = uuid();
                newIds[id] = newId;

                const { overridden_by } = modifiedItem;
                // Check if the `overridden_by.id` is part of the newIds,
                // thus we know if the id should be updated, because it's pointing
                // to an archived entry.
                const shouldOverriddenByIdBeUpdated =
                  overridden_by && newIds[overridden_by.id];

                const newItem = {
                  ...modifiedItem,
                  id: newId,
                  ...(shouldOverriddenByIdBeUpdated && {
                    overridden_by: {
                      ...overridden_by,
                      id: newIds[overridden_by.id],
                    },
                  }),
                };

                newModifiedListItems[initialItem.id] = {
                  ...initialItem,
                  archived: moment().toISOString(),
                };

                if (type === ListItemType.Standard) {
                  const dayOfWeek = newListItems[listItemIndex].recurring
                    ?.day_of_week as number;
                  const startDate = moment()
                    .isoWeekday(dayOfWeek)
                    .add(1, 'week');
                  newItem.start_date = getDateAsUTC(startDate).toISOString();
                } else {
                  // If the exception resolved a conflict, the id in the overridden_by
                  // property of the overridden item should be updated as well.
                  newListItems.forEach((newListItem) => {
                    const { id: listItemId, overridden_by } = newListItem;

                    if (overridden_by?.id === modifiedItem.id) {
                      const overriddenItemIndex = newListItems.findIndex(
                        ({ id: itemId }) => itemId === listItemId,
                      );
                      const newOverriddenItem = {
                        ...newListItem,
                        overridden_by: {
                          ...overridden_by,
                          id: newId,
                        },
                      };

                      newModifiedListItems[listItemId] = newOverriddenItem;
                      newListItems[overriddenItemIndex] = newOverriddenItem;
                    }
                  });
                }

                newListItems[listItemIndex] = newItem;
                newModifiedListItems[newId] = newItem;
              } else {
                // If modified, update the list
                newListItems[listItemIndex] = modifiedItem;
                newModifiedListItems[id] = modifiedItem;
              }
            } else {
              // If new item, add it to the list
              newListItems.push(modifiedItem);
              newModifiedListItems[id] = modifiedItem;
            }
          }
        });

        return {
          listItems: {
            ...prevState.listItems,
            [type]:
              type === ListItemType.Standard
                ? sortOpeningHourItems(newListItems, 'standard')
                : sortOpeningHourItems(newListItems, 'exception'),
          },
          modifiedListItems: {
            ...prevState.modifiedListItems,
            [type]: newModifiedListItems,
          },
          ...(type === ListItemType.Exception && {
            conflicts: getConflicts(newListItems),
          }),
        };
      });
    },
    [scheduleData, setState],
  );

  const handleSetErrors = useCallback(
    (type: ListItemType, errors: Errors) =>
      void setIntervalsErrors((prevState) => ({
        ...prevState,
        [type]: errors,
      })),
    [],
  );

  const handleFetchTemplates = useCallback(async () => {
    try {
      const {
        data: { templates: templatesList },
      } = await ajaxSend<Response<{ templates: Template[] }>>({
        path: '/v1/templating/templates',
      });

      setTemplates(
        templatesList.filter(({ type }) => type === 'email_auto_reply'),
      );
    } catch (error) {
      console.warn(error);

      notify(
        {
          title: 'An error occurred when trying to fetch templates.',
        },
        NotificationType.Error,
      );
    }
  }, [notify]);

  return (
    <ScheduleContext.Provider
      value={{
        activeView,
        autoReply,
        conflicts,
        holidays: holidaysResponse?.data?.holidays || [],
        initialSchedule: scheduleData,
        intervalsErrors,
        listItems,
        meta: {
          isError,
        },
        modifiedListItems,
        announcements,
        templates,
        fetchTemplates: handleFetchTemplates,
        getHolidays: handleFetchHolidays,
        refetchSchedules: handleFetchSchedule,
        refetchAudioMessages,
        setActiveView: (activeView) => setState({ activeView }),
        setAutoReply: (autoReply) => setState({ autoReply }),
        setInitialState: () =>
          setState({ modifiedListItems: initialState.modifiedListItems }),
        setIntervalsErrors: handleSetErrors,
        setModifiedListItems: handleSetModifiedListItems,
      }}
    >
      {children}
    </ScheduleContext.Provider>
  );
}

/** A hook exposing the context functionality. */
export const useScheduleContext = (): ScheduleContext => {
  const context = useContext(ScheduleContext);

  if (!context) {
    throw new Error('Missing ScheduleContext');
  }

  return context;
};
